import { useEffect, useRef, useState } from "react";
import { X, Upload, Sparkles, RefreshCw, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const MATERIALS = [
  {
    group: "LVP",
    items: [
      { id: "lvp-light-oak", label: "Light Oak", swatch: "#c9a980" },
      { id: "lvp-weathered-oak", label: "Weathered Oak", swatch: "#8a7a68" },
      { id: "lvp-espresso", label: "Espresso", swatch: "#3d2b1f" },
      { id: "lvp-gray-wash", label: "Gray Wash", swatch: "#7c7d7d" },
      { id: "lvp-whitewashed", label: "Whitewashed", swatch: "#dccfb8" },
      { id: "lvp-honey", label: "Honey", swatch: "#c68a4a" },
      { id: "lvp-walnut", label: "Walnut", swatch: "#4a2f21" },
      { id: "lvp-reclaimed-barnwood", label: "Reclaimed Barnwood", swatch: "#7a6553" },
    ],
  },
  {
    group: "Laminate",
    items: [
      { id: "laminate-natural-oak", label: "Natural Oak", swatch: "#b58c5a" },
      { id: "laminate-hickory", label: "Hickory", swatch: "#8b5a2b" },
      { id: "laminate-cherry", label: "Cherry", swatch: "#7a3a26" },
      { id: "laminate-dark-oak", label: "Dark Oak", swatch: "#3a271b" },
    ],
  },
  {
    group: "Tile",
    items: [
      { id: "tile-marble-white", label: "White Marble", swatch: "#e8e4dd" },
      { id: "tile-herringbone-marble", label: "Herringbone Marble", swatch: "#d4cec4" },
      { id: "tile-hexagon-black-white", label: "Hexagon B/W", swatch: "#1a1a1a" },
      { id: "tile-subway-classic", label: "Subway White", swatch: "#f0eee9" },
      { id: "tile-dark-slate", label: "Dark Slate", swatch: "#3a3d42" },
      { id: "tile-beige-stone", label: "Beige Stone", swatch: "#c8b899" },
      { id: "tile-wood-plank-tile", label: "Wood-Plank Tile", swatch: "#a67c52" },
      { id: "tile-terracotta", label: "Terracotta", swatch: "#b56b45" },
      { id: "tile-penny-round", label: "Penny Round", swatch: "#efece5" },
    ],
  },
];

export default function VisualizerModal({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [material, setMaterial] = useState("lvp-light-oak");
  const [customPrompt, setCustomPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const resetAll = () => {
    setFile(null);
    setPreviewUrl(null);
    setResultUrl(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPG or PNG).");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image is too large — please pick one under 8 MB.");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResultUrl(null);
  };

  const runVisualize = async () => {
    if (!file) {
      toast.error("Upload a room photo first.");
      return;
    }
    if (material === "custom" && !customPrompt.trim()) {
      toast.error("Describe your custom floor first (e.g. 'wide-plank white oak with knots').");
      return;
    }
    setLoading(true);
    setResultUrl(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("material", material);
      if (material === "custom") {
        fd.append("custom_prompt", customPrompt.trim());
      }
      const resp = await fetch(`${API}/api/visualize`, {
        method: "POST",
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Visualizer error (${resp.status})`);
      }
      const data = await resp.json();
      setResultUrl(`data:${data.mime_type};base64,${data.image_base64}`);
      toast.success("Here's your room with the new floor!");
    } catch (e) {
      toast.error(e.message || "Something went wrong. Try another photo.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Room visualizer"
      data-testid="visualizer-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
    >
      {/* backdrop */}
      <button
        aria-label="Close visualizer"
        onClick={handleClose}
        className="absolute inset-0 bg-stone-950/70 backdrop-blur-sm cursor-default"
      />

      <div className="relative bg-stone-50 rounded-t-3xl sm:rounded-3xl w-full sm:w-[92%] max-w-5xl max-h-[92vh] overflow-y-auto shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-stone-50/95 backdrop-blur-md border-b border-stone-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center">
              <Sparkles size={16} className="text-stone-50" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-display font-semibold text-lg text-stone-900 leading-tight">
                See Your New Floor
              </div>
              <div className="text-xs text-stone-500 leading-tight">
                Upload a photo · pick a material · AI shows the transformation
              </div>
            </div>
          </div>
          <button
            data-testid="visualizer-close"
            onClick={handleClose}
            className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-stone-700" />
          </button>
        </div>

        <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left — controls */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">
                1. Your Room Photo
              </div>
              {previewUrl ? (
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-stone-200 border border-stone-200">
                  <img
                    src={previewUrl}
                    alt="Uploaded room"
                    className="w-full h-full object-cover"
                    data-testid="visualizer-preview"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-3 right-3 bg-stone-900/80 backdrop-blur text-stone-50 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-stone-900 transition-colors"
                    data-testid="visualizer-change-photo"
                  >
                    Change photo
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="visualizer-upload-btn"
                  className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-stone-300 hover:border-orange-500 hover:bg-orange-50/40 flex flex-col items-center justify-center gap-3 text-stone-600 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
                    <Upload size={20} strokeWidth={2} />
                  </div>
                  <div className="font-semibold text-sm">Upload a room photo</div>
                  <div className="text-xs text-stone-500 max-w-[220px] text-center leading-relaxed">
                    Straight-on shots of the floor work best. JPG or PNG, under 8 MB.
                  </div>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="visualizer-file-input"
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">
                2. Pick a Material
              </div>
              <div className="space-y-4">
                {MATERIALS.map((g) => (
                  <div key={g.group}>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-orange-600 mb-2">
                      {g.group}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {g.items.map((m) => {
                        const active = material === m.id;
                        return (
                          <button
                            key={m.id}
                            onClick={() => setMaterial(m.id)}
                            data-testid={`visualizer-material-${m.id}`}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold border transition-all ${
                              active
                                ? "bg-stone-900 border-stone-900 text-stone-50"
                                : "bg-white border-stone-200 text-stone-800 hover:border-stone-400"
                            }`}
                          >
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0"
                              style={{ background: m.swatch }}
                            />
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Custom prompt */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-orange-600 mb-2">
                    Describe Your Own
                  </div>
                  <button
                    onClick={() => setMaterial("custom")}
                    data-testid="visualizer-material-custom"
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold border transition-all mb-2 ${
                      material === "custom"
                        ? "bg-stone-900 border-stone-900 text-stone-50"
                        : "bg-white border-stone-200 text-stone-800 hover:border-stone-400"
                    }`}
                  >
                    <Wand2 size={13} strokeWidth={2.25} />
                    Custom prompt
                  </button>
                  {material === "custom" && (
                    <textarea
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      data-testid="visualizer-custom-prompt"
                      maxLength={300}
                      rows={3}
                      placeholder="e.g. wide-plank white oak with heavy knots, matte finish"
                      className="w-full mt-2 rounded-2xl border border-stone-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 bg-white text-sm text-stone-800 p-3 resize-none"
                    />
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={runVisualize}
              disabled={!file || loading}
              data-testid="visualizer-run"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-orange-500 hover:bg-orange-600 text-stone-50"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Visualizing… ~10s
                </>
              ) : (
                <>
                  <Sparkles size={16} strokeWidth={2.25} />
                  Show me the new floor
                </>
              )}
            </button>
          </div>

          {/* Right — result */}
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">
              3. AI Result
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-stone-200 border border-stone-200">
              {loading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-stone-900/70 text-stone-50 z-10">
                  <Loader2 size={28} className="animate-spin text-orange-400" />
                  <div className="text-sm font-semibold">Reimagining your floor…</div>
                  <div className="text-xs text-stone-300 max-w-xs text-center">
                    Gemini image AI is placing the new material into your room. Takes about 10 seconds.
                  </div>
                </div>
              )}
              {resultUrl ? (
                <img
                  src={resultUrl}
                  alt="AI-visualized room with new floor"
                  className="w-full h-full object-cover"
                  data-testid="visualizer-result"
                />
              ) : (
                !loading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-400 p-6 text-center">
                    <Sparkles size={22} strokeWidth={1.75} />
                    <div className="text-sm font-medium">
                      Upload a room photo and pick a material to see the transformation
                    </div>
                  </div>
                )
              )}
            </div>

            {resultUrl && (
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={resultUrl}
                  download="ml-flooring-visualization.png"
                  data-testid="visualizer-download"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-stone-900 text-stone-50 hover:bg-stone-800 transition-colors"
                >
                  Download image
                </a>
                <button
                  onClick={runVisualize}
                  data-testid="visualizer-retry"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-white border border-stone-200 text-stone-800 hover:border-stone-400 transition-colors"
                >
                  <RefreshCw size={13} /> Try again
                </button>
                <a
                  href="/#contact"
                  onClick={handleClose}
                  data-testid="visualizer-quote-cta"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-orange-500 text-stone-50 hover:bg-orange-600 transition-colors"
                >
                  Get a real quote →
                </a>
              </div>
            )}

            <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
              AI results are for inspiration only. Every actual install includes free on-site
              consultation, precise measurement, and a written quote. Photos are not stored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
